<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import PageTabs from '@/components/Utils/PageTabs.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiProfileHeader from '@/components/ui/UiProfileHeader.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { formatDate } from '@/utils/format';

// A parent's view of their child's report card for the school year: grades
// per subject and grading period, the general average, and acknowledging
// each period once its grades are released.
const props = defineProps({
  // Row from My Children: id, name, grade_level, picture.
  child: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['acknowledged']);

const store = useStore();
const toast = useToast();

const isSHS = computed(() => ['Grade 11', 'Grade 12'].includes(props.child?.grade_level));
const usesTerms = computed(() => !isSHS.value && store.getters.getSyType == 1);

const grades = shallowRef({});
const acks = shallowRef([]);
const loading = shallowRef(false);
const failed = shallowRef('');

// Senior high: one table per semester. The acknowledgement API names them
// "First Sem" / "Second Sem".
const SEMESTERS = [
  { id: 'First Sem', name: '1st semester', key: 'firstSem' },
  { id: 'Second Sem', name: '2nd semester', key: 'secondSem' },
];
const semester = shallowRef('First Sem');

async function load() {
  if (!props.child) return;
  loading.value = true;
  failed.value = '';
  const year = store.getters.getFilterSelected;
  try {
    const [g, a] = await Promise.all([
      apiRequest('get', `/parent-records/getMyChildrenGrades/${props.child.id}/${year}/${props.child.grade_level}`),
      apiRequest('get', `/parent-records/acknowledgements/${props.child.id}/${year}`).catch(() => []),
    ]);
    grades.value = g && typeof g === 'object' ? g : {};
    acks.value = Array.isArray(a) ? a : [];
  } catch (e) {
    grades.value = {};
    failed.value = errorMessage(e, 'Could not load the report card.');
  } finally {
    loading.value = false;
  }
}

watch(open, (isOpen) => {
  if (!isOpen) return;
  semester.value = 'First Sem';
  ackPeriod.value = null;
  load();
});

const PERIODS = computed(() => {
  if (isSHS.value) return ['1st Quarter', '2nd Quarter'];
  return usesTerms.value
    ? ['1st Term', '2nd Term', '3rd Term']
    : ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'];
});
const shortPeriod = (p) => p.replace(' Quarter', ' Q').replace(' Term', ' T');

const rows = computed(() => {
  if (isSHS.value) {
    const sem = SEMESTERS.find((s) => s.id === semester.value);
    return grades.value.seniorHigh?.[sem.key] ?? [];
  }
  return grades.value.juniorHigh ?? [];
});
// The report card API ends each list with a "General Average" row.
const subjectRows = computed(() => rows.value.filter((r) => r && r.subject && r.subject !== 'General Average'));
const averageRow = computed(() => rows.value.find((r) => r?.subject === 'General Average'));
// The server tags a subject's components (e.g. MAPEH's) with componentOf.
const isComponent = (row) => !!row.componentOf;

const shown = (v) => (v === null || v === undefined || v === '' || v === '-' ? '—' : v);
const tone = (v) => {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || v === '-' || Number.isNaN(n)) return '';
  return n < 75 ? 'rc-grade--risk' : n < 80 ? 'rc-grade--warn' : '';
};

// Periods with at least one grade released, in order.
const releasedPeriods = computed(() =>
  PERIODS.value.filter((p) => subjectRows.value.some((r) => shown(r[p]) !== '—')),
);
const generalAverage = computed(() => {
  const finals = subjectRows.value.filter((r) => !isComponent(r)).map((r) => Number(r.finalGrade)).filter((n) => !Number.isNaN(n) && n > 0);
  if (averageRow.value?.finalGrade != null) return averageRow.value.finalGrade;
  return finals.length ? Math.round(finals.reduce((a, b) => a + b, 0) / finals.length) : null;
});
const periodAverage = (p) => {
  const list = subjectRows.value.filter((r) => !isComponent(r)).map((r) => Number(r[p])).filter((n) => !Number.isNaN(n) && n > 0);
  return list.length ? (list.reduce((a, b) => a + b, 0) / list.length).toFixed(2) : null;
};

// --- Acknowledgement ------------------------------------------------------
const ackFor = (p) =>
  acks.value.find((a) => a.period === p && (!isSHS.value || a.semester === semester.value)) || null;
const ackPeriod = shallowRef(null);
const reviewed = shallowRef(false);
const ready = shallowRef(false);
const submitting = shallowRef(false);

function startAck(p) {
  ackPeriod.value = p;
  reviewed.value = false;
  ready.value = false;
}

async function acknowledge() {
  submitting.value = true;
  try {
    assertOk(
      await apiRequest('post', '/parent-records/parentAcknowledgement', {
        studentID: props.child.id,
        school_yearID: store.getters.getFilterSelected,
        period: ackPeriod.value,
        semester: isSHS.value ? semester.value : null,
      }),
    );
    toast.success(`${ackPeriod.value} grades acknowledged.`);
    ackPeriod.value = null;
    emit('acknowledged');
    load();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not record the acknowledgement.'));
  } finally {
    submitting.value = false;
  }
}

const photo = computed(() => {
  const name = String(props.child?.picture ?? '');
  return name && !name.startsWith('default')
    ? `${process.env.VUE_APP_SERVER}/enroll-student/view/studentFile/${encodeURIComponent(name)}`
    : '';
});
</script>

<template>
  <UiDialog v-model="open" :title="`${child?.name || 'Your child'}'s report card`" size="xl" :persistent="false">
    <template #header>
      <UiProfileHeader
        :name="child?.name || ''"
        :photo="photo"
        :subtitle="['Report card', child?.grade_level].filter(Boolean).join(' · ')"
        :size="48"
      >
        <UiBadge v-if="generalAverage" :variant="Number(generalAverage) >= 75 ? 'green' : 'red'">
          General average {{ generalAverage }}
        </UiBadge>
      </UiProfileHeader>
    </template>

    <PageTabs v-if="isSHS" v-model="semester" :items="SEMESTERS" class="rc-tabs" />

    <div v-if="loading" class="rc-empty">Loading…</div>
    <UiEmptyState v-else-if="failed" icon="mdi-cloud-off-outline" title="The report card couldn't be loaded" :description="failed" />
    <UiEmptyState
      v-else-if="!subjectRows.length"
      icon="mdi-file-chart-outline"
      title="No grades yet"
      description="Grades appear here once your child's adviser releases them."
    />
    <template v-else>
      <div class="rc-table">
        <table>
          <thead>
            <tr>
              <th class="rc-subject">Subject</th>
              <th v-for="p in PERIODS" :key="p" class="rc-num">{{ shortPeriod(p) }}</th>
              <th class="rc-num">Final</th>
              <th class="rc-num">Remarks</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in subjectRows" :key="`${row.subject}-${i}`" :class="{ 'rc-component': isComponent(row) }">
              <td class="rc-subject">{{ row.subject }}</td>
              <td v-for="p in PERIODS" :key="p" class="rc-num" :class="tone(row[p])">{{ shown(row[p]) }}</td>
              <td class="rc-num rc-final" :class="tone(row.finalGrade)">{{ shown(row.finalGrade) }}</td>
              <td class="rc-num">
                <UiBadge v-if="row.remarks && row.remarks !== '-'" :variant="row.remarks === 'Passed' ? 'green' : 'red'">
                  {{ row.remarks }}
                </UiBadge>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="rc-subject">General average</td>
              <td v-for="p in PERIODS" :key="p" class="rc-num">{{ shown(periodAverage(p)) }}</td>
              <td class="rc-num rc-final" :class="tone(generalAverage)">{{ shown(generalAverage) }}</td>
              <td class="rc-num">
                <UiBadge v-if="generalAverage" :variant="Number(generalAverage) >= 75 ? 'green' : 'red'">
                  {{ Number(generalAverage) >= 75 ? 'Passed' : 'Failed' }}
                </UiBadge>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p class="rc-note">Grades below 75 are in red, 75–79 in amber. Components (such as MAPEH's) are indented under their subject.</p>

      <!-- Acknowledge each released period -->
      <section class="rc-ack">
        <h3 class="rc-ack__title">Acknowledge grades</h3>
        <p class="rc-ack__hint">Confirm you've seen each grading period's grades. The school keeps a record of when you did.</p>
        <div class="rc-ack__periods">
          <div v-for="p in PERIODS" :key="p" class="rc-ack__period">
            <span class="rc-ack__name">{{ p }}</span>
            <UiBadge v-if="ackFor(p)" variant="green">
              <v-icon size="13">mdi-check</v-icon>
              Acknowledged {{ formatDate(ackFor(p).createdAt, 'MMM D') }}
            </UiBadge>
            <v-btn
              v-else-if="releasedPeriods.includes(p)"
              size="small"
              :variant="ackPeriod === p ? 'flat' : 'outlined'"
              :color="ackPeriod === p ? 'primary' : undefined"
              @click="startAck(p)"
            >
              Acknowledge
            </v-btn>
            <span v-else class="rc-muted">Not released yet</span>
          </div>
        </div>

        <div v-if="ackPeriod" class="rc-ack__form">
          <p class="rc-ack__summary">
            <strong>{{ ackPeriod }}</strong> average: <strong>{{ shown(periodAverage(ackPeriod)) }}</strong>
          </p>
          <v-checkbox v-model="reviewed" density="compact" hide-details>
            <template #label>I have reviewed my child's {{ ackPeriod }} grades shown above.</template>
          </v-checkbox>
          <v-checkbox v-model="ready" density="compact" hide-details>
            <template #label>These are the grades I received for review, and I acknowledge them for this grading period.</template>
          </v-checkbox>
          <div class="rc-ack__actions">
            <v-btn variant="text" @click="ackPeriod = null">Cancel</v-btn>
            <v-btn color="primary" :loading="submitting" :disabled="!reviewed || !ready" @click="acknowledge">
              Acknowledge {{ ackPeriod }}
            </v-btn>
          </div>
        </div>
      </section>
    </template>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.rc-tabs {
  margin-bottom: 12px;
}
.rc-table {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.rc-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.rc-table th,
.rc-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
}
.rc-table thead th {
  font-size: 13px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.rc-table tfoot td {
  border-bottom: 0;
  font-weight: 600;
  background: var(--subtle);
}
.rc-subject {
  min-width: 200px;
  text-align: left;
}
.rc-component .rc-subject {
  padding-left: 28px;
  color: var(--muted-foreground);
}
.rc-num {
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.rc-final {
  font-weight: 600;
}
.rc-grade--warn {
  color: #b45309;
}
.rc-grade--risk {
  color: #b91c1c;
}
html.dark .rc-grade--warn {
  color: #fcd34d;
}
html.dark .rc-grade--risk {
  color: #fca5a5;
}
.rc-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--muted-foreground);
}
.rc-muted {
  font-size: 13px;
  color: var(--muted-foreground);
}
.rc-ack {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.rc-ack__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.rc-ack__hint {
  margin: 4px 0 12px;
  font-size: 13px;
  color: var(--muted-foreground);
}
.rc-ack__periods {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 8px;
}
.rc-ack__period {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.rc-ack__name {
  font-size: 14px;
  font-weight: 500;
}
.rc-ack__form {
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  background: var(--subtle);
}
.rc-ack__summary {
  margin: 0 0 4px;
  font-size: 14px;
}
.rc-ack__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.rc-empty {
  padding: 32px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
</style>
